/**
 * Edge Queue — admin control panel for the nixor-edge task queue.
 *
 * Trigger tasks, watch pending + running tasks live (3s poll), cancel a
 * pending task, or clear the entire pending queue. The nixor-edge
 * TRIGGER_API_KEY stays server-side (the worker proxies everything under
 * /api/nixor/* with admin auth).
 *
 * Honest constraint wired into the UI: a running task can't be cancelled at
 * the nixor-edge level (the QueueDO splices it on claim, so `remove` is a
 * no-op for running tasks). The Cancel button only renders for `pending`.
 */
import { useState } from 'react';
import { AlertTriangle, KeyRound, Loader2, Network, Send, Trash2, XCircle } from 'lucide-react';
import {
  cancelNixorTask,
  clearNixorQueue,
  triggerNixorTask,
  useNixorTasks,
} from '../lib/data';
import { Skeleton, SkeletonListCard } from './Skeleton';

function formatAge(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return 'just now';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

function statusPill(status: string): string {
  if (status === 'running')
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (status === 'pending')
    return 'bg-blue-50 text-blue-700 border-blue-200';
  return 'bg-slate-50 text-slate-600 border-slate-200';
}

interface EdgeQueueProps {
  currentUser?: { role?: 'Admin' | 'Reviewer' | null } | null;
}

export function EdgeQueue({ currentUser }: EdgeQueueProps) {
  const isAdmin = currentUser?.role === 'Admin';

  if (!isAdmin) {
    return (
      <div className="space-y-6 animate-fadeIn" id="edge-queue-view">
        <div className="p-12 text-center space-y-3">
          <div className="inline-flex p-3 bg-amber-50 rounded-full text-amber-600 border border-amber-100">
            <KeyRound className="w-6 h-6" />
          </div>
          <h3 className="text-sm font-semibold text-slate-800">Administrator Access Required</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
            You are currently logged in as a <strong>Reviewer</strong>. Only <strong>Admin</strong> users are
            authorized to trigger tasks on the nixor-edge control plane or clear the queue.
          </p>
        </div>
      </div>
    );
  }

  return <EdgeQueueAdmin />;
}

function EdgeQueueAdmin() {
  const { data, loading, error, retry } = useNixorTasks();
  const [prompt, setPrompt] = useState('');
  const [labelsRaw, setLabelsRaw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const handleTrigger = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || submitting) return;
    setSubmitting(true);
    setMessage(null);
    try {
      let labels: Record<string, string> | undefined;
      const trimmedLabels = labelsRaw.trim();
      if (trimmedLabels) {
        try {
          labels = JSON.parse(trimmedLabels);
        } catch {
          labels = Object.fromEntries(
            trimmedLabels.split(',').map((kv) => {
              const [k, ...rest] = kv.split(':');
              return [k.trim(), rest.join(':').trim()];
            }).filter(([k, v]) => k && v),
          ) as Record<string, string>;
        }
      }
      const res = await triggerNixorTask(prompt.trim(), labels);
      setMessage({ kind: 'ok', text: `Queued task ${res.taskId}` });
      setPrompt('');
      retry();
    } catch (err: any) {
      setMessage({ kind: 'err', text: err?.message ?? 'Trigger failed' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (taskId: string) => {
    if (busyId) return;
    setBusyId(taskId);
    setMessage(null);
    try {
      await cancelNixorTask(taskId);
      setMessage({ kind: 'ok', text: `Cancelled ${taskId.slice(0, 8)}` });
      retry();
    } catch (err: any) {
      setMessage({ kind: 'err', text: err?.message ?? 'Cancel failed' });
    } finally {
      setBusyId(null);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear the entire pending queue on nixor-edge? Running tasks are not affected.')) return;
    setMessage(null);
    try {
      await clearNixorQueue();
      setMessage({ kind: 'ok', text: 'Pending queue cleared' });
      retry();
    } catch (err: any) {
      setMessage({ kind: 'err', text: err?.message ?? 'Clear failed' });
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" id="edge-queue-view">
      {/* Header */}
      <div className="bg-white border border-[#EDEDEB] rounded-xl p-4 flex items-center gap-4 justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg border bg-blue-50 text-blue-600 border-blue-100">
            <Network className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-mono text-slate-400 uppercase tracking-wider font-semibold">nixor-edge queue</div>
            <div className="text-xl font-bold text-[#191919]">
              {loading && !data ? '…' : (data?.queueLength ?? 0)} pending
            </div>
            <div className="text-[11px] text-slate-400">
              {data ? `${data.tasks.filter((t) => t.status === 'running').length} running` : ''}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClear}
          disabled={!data?.queueLength}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Clear pending queue
        </button>
      </div>

      {/* Trigger form */}
      <form onSubmit={handleTrigger} className="bg-white border border-[#EDEDEB] rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Send className="w-4 h-4 text-blue-600" />
          <h3 className="font-bold text-[#191919] text-sm">Trigger a task</h3>
          <span className="text-[11px] text-[#91918E] ml-2">POST /api/trigger (via worker proxy)</span>
        </div>
        <textarea
          required
          rows={3}
          placeholder="Prompt to send to the nixor-edge runner…"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="w-full bg-white border border-[#EDEDEB] focus:border-[#191919] rounded-lg px-3.5 py-2.5 text-[#191919] text-xs font-sans outline-none transition resize-none leading-relaxed"
        />
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-[10px] font-bold text-[#91918E] uppercase tracking-wider font-mono mb-1">
              Labels (optional JSON or <code>k:v,k:v</code>)
            </label>
            <input
              type="text"
              placeholder='e.g. {"source":"ui"} or source:ui,env:dev'
              value={labelsRaw}
              onChange={(e) => setLabelsRaw(e.target.value)}
              className="w-full bg-white border border-[#EDEDEB] focus:border-[#191919] rounded-lg px-3 py-2 text-[#191919] text-xs font-mono outline-none transition"
            />
          </div>
          <button
            type="submit"
            disabled={!prompt.trim() || submitting}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#191919] hover:bg-[#2A2A28] disabled:opacity-50 text-white text-xs font-semibold transition"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Trigger
          </button>
        </div>
      </form>

      {/* Toast */}
      {message && (
        <div
          className={`p-3 rounded-lg border text-xs flex items-center gap-2 ${
            message.kind === 'ok'
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-rose-50 text-rose-700 border-rose-200'
          }`}
        >
          {message.kind === 'ok' ? null : <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
          <span className="font-mono">{message.text}</span>
        </div>
      )}

      {/* Live list */}
      <div className="bg-white border border-[#EDEDEB] rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[#EDEDEB] flex items-center gap-2">
          <Network className="w-4 h-4 text-blue-600" />
          <h3 className="font-bold text-[#191919] text-sm">Pending + running</h3>
          <span className="text-[11px] text-[#91918E] ml-2">Polling every 3s · Cancel only removes pending (running is already claimed by the runner)</span>
        </div>
        {loading && !data ? (
          <SkeletonListCard rows={3} titleWidth={140} />
        ) : error && !data ? (
          <div className="p-6 text-center space-y-1">
            <div className="text-xs font-semibold text-rose-700">Couldn't reach nixor-edge</div>
            <div className="font-mono text-[11px] text-rose-500 break-all">{error}</div>
            <div className="text-[11px] text-slate-400">Is NIXOR_EDGE_TRIGGER_KEY set on the worker?</div>
          </div>
        ) : (
          <div className="divide-y divide-[#EDEDEB]">
            {(!data || data.tasks.length === 0) ? (
              <div className="p-6 text-center text-xs text-slate-400">
                Queue is empty. Trigger a task above to populate it.
              </div>
            ) : (
              data.tasks.map((t) => {
                const ageMs = Date.now() - t.createdAt;
                const canCancel = t.status === 'pending';
                return (
                  <div key={t.taskId} className="p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${statusPill(t.status)}`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            t.status === 'running'
                              ? 'bg-emerald-500 animate-pulse'
                              : t.status === 'pending'
                              ? 'bg-blue-400'
                              : 'bg-slate-300'
                          }`}
                        />
                        {t.status}
                      </span>
                      <span className="font-mono text-xs text-[#191919] truncate" title={t.prompt}>
                        {t.prompt}
                      </span>
                      <span className="font-mono text-[10px] text-slate-400 shrink-0" title={t.taskId}>
                        {t.taskId.slice(0, 8)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-[11px] font-mono text-slate-400">{formatAge(ageMs)}</span>
                      {canCancel ? (
                        <button
                          type="button"
                          onClick={() => handleCancel(t.taskId)}
                          disabled={busyId === t.taskId}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-[10px] font-semibold hover:bg-rose-100 disabled:opacity-50 transition"
                          title="Remove from the pending queue"
                        >
                          {busyId === t.taskId ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <XCircle className="w-3 h-3" />
                          )}
                          Cancel
                        </button>
                      ) : (
                        <span className="text-[10px] text-slate-400 italic">running — cannot cancel</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
